import { useEffect, useRef, useState } from "react";
import { useGetSnmpAgentQuery, useUpdateSnmpAgentMutation } from "../../../../store/api/admin/adminApiSlice";
import { ISnmpAgent } from "../../../../store/api/admin/types";
import { selectedSnmpAgentSelector } from "../../../../store/api/admin/selectors";
import { useSelector } from "react-redux";
import { useStoreDispatch } from "../../../../store/hooks";
import { setConfirmDialog, setOpenToast } from "../../../../store/helpers/helperReducers";
import { snmpAgentPayload } from "../../../helpers";
import { isValidPassword } from "../../../../utils/validations";
import { AuthenticationOptions, PrivacyOptions } from "../../../../globals/constants";
import { setSelectedSnmpAgent } from "../../../../store/api/admin/reducers";
import { DIALOGOPENFOR } from "../../../../globals/globals";
import { errorMessage } from "../../../../utils/helpers";
import { setErrorMsg } from "../../../../store/helpers/helperReducers";
import { errorMsgSelector } from "../../../../store/helpers/helperSelectors";
import { store } from "../../../../store";

interface IPayload {
  [key: string]: string;
}

export const useSnmpAgentData = () => {
  const dispatch = useStoreDispatch();
  const snmpAgentDetails = useSelector(selectedSnmpAgentSelector);
  const [errMsg, setErrMsg] = useState<IPayload>({});
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const [isPrivChecked, setIsPrivChecked] = useState(false);
  const [enableV2, setEnableV2] = useState(false);
  const [enableV3, setEnableV3] = useState(false);
  const [agentEnable, setAgentEnable] = useState(false);
  const [agentVersion, setAgentVersion] = useState(snmpAgentDetails?.agentVersion);
  const [snmpAgentData, setSnmpAgentData] = useState<ISnmpAgent>(snmpAgentDetails);
  const [authPassword, setAuthPassword] = useState("");
  const [privPassword, setPrivPassword] = useState("");
  const [authConfirmPassword, setAuthConfirmPassword] = useState("");
  const [privConfirmPassword, setPrivConfirmPassword] = useState("");

  let errCount = 0;

  Object.keys(errMsg).forEach((key) => {
    if (typeof errMsg[key] === "string" && errMsg[key].trim() !== "") {
      errCount++;
    }
  });

  const errorMsg = useSelector(errorMsgSelector);
  const port = "port";
  const readComunity = "readComunity";
  const writeComunity = "writeComunity";
  const engineId = "engineId";
  const userName = "userName";
  const authPasswords = "authPassword";
  const authConfirmPasswords = "authConfirmPassword";
  const privPasswords = "privPassword";
  const privConfirmPasswords = "privConfirmPassword";
  const clearInputList = { port: "", readComunity: "", writeComunity: "", engineId: "", userName: "", authPasswords: "", authConfirmPasswords: "", privPasswords: "", privConfirmPasswords: "" }

  useEffect(() => {
    setErrMsg({});
    dispatch(setErrorMsg({ ...errorMsg, ...clearInputList }));
  }, []);

  const { data, isLoading, refetch } = useGetSnmpAgentQuery();

  const [updateSnmpAgent] = useUpdateSnmpAgentMutation();

  const initialStateRef = useRef<{
    snmpAgentData: ISnmpAgent;
    agentEnable: boolean;
    enableV2: boolean;
    enableV3: boolean;
  } | null>(null);

  const handleUpdateSnmpVersion = (version: string) => {
    if (version === "V2") {
      setEnableV2(true);
    } else if (version === "V3") {
      setEnableV3(true);
    } else if (version === "BOTH") {
      setEnableV2(true);
      setEnableV3(true);
    } else if (version === "NONE") {
      setEnableV2(false);
      setEnableV3(false);
    }
  };

  const handleResetSnmpAgent = () => {
    if (!initialStateRef.current) return;

    const init = initialStateRef.current;

    setSnmpAgentData(init.snmpAgentData);
    setAgentEnable(init.agentEnable);
    setEnableV2(init.enableV2);
    setEnableV3(init.enableV3);

    setIsAuthChecked(false);
    setIsPrivChecked(false);
    setAuthPassword("");
    setPrivPassword("");
    setAuthConfirmPassword("");
    setPrivConfirmPassword("");
    setErrMsg({});
    dispatch(setErrorMsg({ ...errorMsg, ...clearInputList }));
  };

  useEffect(() => {
    if (data) {
      // handle dispatch to store data in snmpAgent state
      let obj = {
        agentVersion: data?.agentVersion,
        port: data?.port,
        readComunity: data?.readComunity,
        writeComunity: data?.writeComunity,
        engineId: data?.engineId,
        userName: data?.userName,
        authProtocol: data?.authProtocol,
        privProtocol: data?.privProtocol,
        authPassword: data?.authPassword || "",
        privPassword: data?.privPassword || "",
      };
      dispatch(setSelectedSnmpAgent(obj));
      setSnmpAgentData(obj);
      setIsAuthChecked(false);
      setIsPrivChecked(false);
      
      let v2 = false;
      let v3 = false;
      let agentEnabled = false;
      
      if (data?.agentVersion === "V2") {
        v2 = true;
        agentEnabled = true;
      } else if (data?.agentVersion === "V3") {
        v3 = true;
        agentEnabled = true;
      } else if (data?.agentVersion === "BOTH") {
        v2 = true;
        v3 = true;
        agentEnabled = true;
      }
      
      setEnableV2(v2);
      setEnableV3(v3);
      setAgentEnable(agentEnabled);
      
      // Update initialStateRef to the new saved state
      initialStateRef.current = {
        snmpAgentData: obj,
        agentEnable: agentEnabled,
        enableV2: v2,
        enableV3: v3,
      };
    }
  }, [data, dispatch]);

  const isEngineIdValidString = (str: string, name: string) => {
    if (!str.startsWith("0x")) {
      return { [name]: `Should start with 0x` };
    }
    if (str.length < 10) {
      return { [name]: `Max length should be 10` };
    }
    if (str.length % 2 !== 0) {
      return { [name]: `Total characters should be even` };
    }
    return null;
  };

  const onBlurHandler = (event: any) => {
    const { name, value } = event?.target;
    const existing = store.getState().helpers.errorMsg;

    const setErr = (field: string, msg: string) => {
      dispatch(setErrorMsg({ ...existing, [field]: msg }));
    };

    if (name === "snmpAgentEnable" && !agentEnable) {
      setErr(name, `Please enable Agent!!`);
    }
    if (agentEnable && enableV3 && name === "engineId") {
      const errorObj = isEngineIdValidString(value, name);
      if (errorObj) {
        dispatch(setErrorMsg({ ...existing, ...errorObj }));
      }
      return;
    }
    if ((name === "authPassword" && isAuthChecked) || (name === "authConfirmPassword" && isAuthChecked)) {
      if (!!value) {
        const isValidPass = isValidPassword(value);
        if (!isValidPass) {
          setErr(name, `Password is invalid. It must meet the specified criteria!`);
        }
      }
    }
    if ((name === "privPassword" && isPrivChecked) || (name === "privConfirmPassword" && isPrivChecked)) {
      if (!!value) {
        const isValidPass = isValidPassword(value);
        if (!isValidPass) {
          setErr(name, `Password is invalid. It must meet the specified criteria!`);
        }
      }
    }
  };

  const handleMsg = () => {
    if (
      enableV3 &&
      snmpAgentData?.authProtocol !== snmpAgentDetails?.authProtocol &&
      snmpAgentData?.authProtocol.toLowerCase() !== "no_auth" &&
      !authPassword
    ) {
      return "Authentication Protocol is selected and Password length is less than 8.";
    }
    if (
      enableV3 &&
      snmpAgentData?.privProtocol !== snmpAgentDetails?.privProtocol &&
      snmpAgentData?.privProtocol.toLowerCase() !== "no_priv" &&
      !privPassword
    ) {
      return "Privacy Protocol is selected and Password length is less than 8.";
    }
    if (
      enableV3 &&
      snmpAgentData?.authProtocol?.toLowerCase() !== "no_auth" &&
      !isAuthChecked &&
      !snmpAgentData?.authPassword
    ) {
      return "Authentication Protocol requires a password. Please set a password.";
    }
    if (
      enableV3 &&
      snmpAgentData?.privProtocol?.toLowerCase() !== "no_priv" &&
      !isPrivChecked &&
      !snmpAgentData?.privPassword
    ) {
      return "Privacy Protocol requires a password. Please set a password.";
    }
    if (agentEnable && !enableV2 && !enableV3) {
      return "Please select Either One of SNMP version V2 or V3";
    }
    if (agentEnable && enableV2 && (!snmpAgentData?.readComunity || !snmpAgentData?.writeComunity)) {
      return "SNMP V2C is selected. Enter both read and write communities.";
    }
    if (agentEnable && enableV3 && !snmpAgentData?.userName) {
      return "User Name is mandatory for SNMP V3";
    }
    if (agentEnable && enableV3 && !snmpAgentData?.engineId) {
      return "Engine ID is mandatory for SNMP V3";
    }
    return "";
  };

  const handleValidateVersion = () => {
    let isValid: boolean = true;
    const existing = store.getState().helpers.errorMsg;
    if (
      agentEnable &&
      snmpAgentData?.authProtocol !== snmpAgentDetails?.authProtocol &&
      snmpAgentData?.authProtocol.toLowerCase() !== "no_auth" &&
      !authPassword
    ) {
      dispatch(setErrorMsg({ ...existing, authProtocol: "Authentication Protocol is selected and Password length is less than 8." }));
      isValid = false;
    }
    if (
      agentEnable &&
      snmpAgentData?.privProtocol !== snmpAgentDetails?.privProtocol &&
      snmpAgentData?.privProtocol.toLowerCase() !== "no_priv" &&
      !privPassword
    ) {
      dispatch(setErrorMsg({ ...store.getState().helpers.errorMsg, privProtocol: "Privacy Protocol is selected and Password length is less than 8." }));
      isValid = false;
    }
    // Validate that password exists when protocol requires it
    if (
      agentEnable &&
      enableV3 &&
      snmpAgentData?.authProtocol?.toLowerCase() !== "no_auth" &&
      !isAuthChecked &&
      !snmpAgentData?.authPassword
    ) {
      dispatch(setErrorMsg({ ...store.getState().helpers.errorMsg, authProtocol: "Authentication Protocol requires a password. Please set a password." }));
      isValid = false;
    }
    if (
      agentEnable &&
      enableV3 &&
      snmpAgentData?.privProtocol?.toLowerCase() !== "no_priv" &&
      !isPrivChecked &&
      !snmpAgentData?.privPassword
    ) {
      dispatch(setErrorMsg({ ...store.getState().helpers.errorMsg, privProtocol: "Privacy Protocol requires a password. Please set a password." }));
      isValid = false;
    }
    // Validate that password exists when protocol requires it
    if (
      agentEnable &&
      enableV3 &&
      snmpAgentData?.authProtocol?.toLowerCase() !== "no_auth" &&
      !isAuthChecked &&
      !snmpAgentData?.authPassword
    ) {
      dispatch(setConfirmDialog(DIALOGOPENFOR.SNMPVERSION));
      isValid = false;
    }
    if (
      agentEnable &&
      enableV3 &&
      snmpAgentData?.privProtocol?.toLowerCase() !== "no_priv" &&
      !isPrivChecked &&
      !snmpAgentData?.privPassword
    ) {
      dispatch(setErrorMsg({ ...store.getState().helpers.errorMsg, privProtocol: "Privacy Protocol is selected and Password length is less than 8." }));
      isValid = false;
    }
    // Validate that password exists when protocol requires it
    if (
      agentEnable &&
      enableV3 &&
      snmpAgentData?.authProtocol?.toLowerCase() !== "no_auth" &&
      !isAuthChecked &&
      !snmpAgentData?.authPassword
    ) {
      dispatch(setErrorMsg({ ...store.getState().helpers.errorMsg, authProtocol: "Authentication Protocol requires a password. Please set a password." }));
      isValid = false;
    }
    if (
      agentEnable &&
      enableV3 &&
      snmpAgentData?.privProtocol?.toLowerCase() !== "no_priv" &&
      !isPrivChecked &&
      !snmpAgentData?.privPassword
    ) {
      dispatch(setErrorMsg({ ...store.getState().helpers.errorMsg, privProtocol: "Privacy Protocol requires a password. Please set a password." }));
      isValid = false;
    }
    // Validate that password exists when protocol requires it
    if (
      agentEnable &&
      enableV3 &&
      snmpAgentData?.authProtocol?.toLowerCase() !== "no_auth" &&
      !isAuthChecked &&
      !snmpAgentData?.authPassword
    ) {
      dispatch(setConfirmDialog(DIALOGOPENFOR.SNMPVERSION));
      isValid = false;
    }
    if (
      agentEnable &&
      enableV3 &&
      snmpAgentData?.privProtocol?.toLowerCase() !== "no_priv" &&
      !isPrivChecked &&
      !snmpAgentData?.privPassword
    ) {
      dispatch(setConfirmDialog(DIALOGOPENFOR.SNMPVERSION));
      isValid = false;
    }
    if (agentEnable && !enableV2 && !enableV3) {
      dispatch(setConfirmDialog(DIALOGOPENFOR.SNMPVERSION));
      isValid = false;
    }
    if (agentEnable && enableV2 && (!snmpAgentData?.readComunity || !snmpAgentData?.writeComunity)) {
      dispatch(setConfirmDialog(DIALOGOPENFOR.SNMPVERSION));
      isValid = false;
    }
    if (agentEnable && enableV3 && !snmpAgentData?.userName) {
      dispatch(setConfirmDialog(DIALOGOPENFOR.SNMPVERSION));
      isValid = false;
    }
    if (agentEnable && enableV3 && !snmpAgentData?.engineId) {
      dispatch(setConfirmDialog(DIALOGOPENFOR.SNMPVERSION));
      isValid = false;
    }
    return isValid;
  };

  const submitHandler = async () => {
    try {
      let result: any;
      const existing = store.getState().helpers.errorMsg;

      // Validate authentication password when checkbox is checked
      if (isAuthChecked && snmpAgentData?.authProtocol?.toLowerCase() !== "no_auth") {
        const trimmedAuthPassword = authPassword?.trim() || "";
        if (!trimmedAuthPassword) {
          dispatch(setErrorMsg({ ...existing, authPassword: "Password is required" }));
          return false;
        }
        if (!isValidPassword(trimmedAuthPassword)) {
          dispatch(setErrorMsg({ ...existing, authPassword: "Password is invalid. It must meet the specified criteria!" }));
          return false;
        }
        if (trimmedAuthPassword !== authConfirmPassword?.trim()) {
          dispatch(setErrorMsg({ ...existing, authConfirmPassword: "Passwords do not match" }));
          return false;
        }
      }

      // Validate privacy password when checkbox is checked
      if (isPrivChecked && snmpAgentData?.privProtocol?.toLowerCase() !== "no_priv") {
        const trimmedPrivPassword = privPassword?.trim() || "";
        if (!trimmedPrivPassword) {
          dispatch(setErrorMsg({ ...existing, privPassword: "Password is required" }));
          return false;
        }
        if (!isValidPassword(trimmedPrivPassword)) {
          dispatch(setErrorMsg({ ...existing, privPassword: "Password is invalid. It must meet the specified criteria!" }));
          return false;
        }
      }
      
      // Validate privacy password when checkbox is checked
      if (isPrivChecked && snmpAgentData?.privProtocol?.toLowerCase() !== "no_priv") {
        const trimmedPrivPassword = privPassword?.trim() || "";
        if (!trimmedPrivPassword) {
          setErrMsg(prev => ({ ...prev, privPassword: "Password is required" }));
          return false;
        }
        if (!isValidPassword(trimmedPrivPassword)) {
          setErrMsg(prev => ({ ...prev, privPassword: "Password is invalid. It must meet the specified criteria!" }));
          return false;
        }
        if (trimmedPrivPassword !== privConfirmPassword?.trim()) {
          setErrMsg(prev => ({ ...prev, privConfirmPassword: "Passwords do not match" }));
          return false;
        }
      }
      
      // Check other validations
      if (!handleValidateVersion()) {
        return false;
      }

      if (agentEnable && enableV3) {
        const errorObj = isEngineIdValidString(snmpAgentData?.engineId, "engineId");
        if (errorObj) {
          dispatch(setErrorMsg({ ...store.getState().helpers.errorMsg, ...errorObj }));
          return false;
        }
      }

      result = await updateSnmpAgent(snmpAgentPayload(snmpAgentData, agentEnable, enableV2, enableV3, authPassword, privPassword, isAuthChecked, isPrivChecked));
      if (result?.error) throw result?.error;
      if (result.data?.status === 200) {
        dispatch(
          setOpenToast({
            openToast: true,
            toastType: "success",
            message: "The data has been  updated for SnmpAgent",
          })
        );
        refetch();
        return true;
      }
      return false;
    } catch (error: any) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
      return false;
    }
  };

  const handleSelectVersion = () => {
    dispatch(setConfirmDialog(undefined));
  };

  return {
    snmpAgentData,
    setSnmpAgentData,
    isLoading,
    errMsg,
    setErrMsg,
    AuthenticationOptions,
    PrivacyOptions,
    isAuthChecked,
    setIsAuthChecked,
    isPrivChecked,
    setIsPrivChecked,
    authConfirmPassword,
    setAuthConfirmPassword,
    privConfirmPassword,
    setPrivConfirmPassword,
    agentVersion,
    setAgentVersion,
    submitHandler,
    handleResetSnmpAgent,
    onBlurHandler,
    errCount,
    agentEnable,
    enableV2,
    enableV3,
    setAgentEnable,
    setEnableV2,
    setEnableV3,
    handleSelectVersion,
    handleMsg,
    handleUpdateSnmpVersion,
    authPassword,
    setAuthPassword,
    privPassword,
    setPrivPassword,
    port,
    readComunity,
    writeComunity,
    engineId,
    userName,
    authPasswords,
    authConfirmPasswords,
    privPasswords,
    privConfirmPasswords,
  };
};
